import { API_HOST } from '@utils/index'
import { BRAND } from '@utils/settings'
import {
  BreadcrumbJsonLd,
  FAQPageJsonLd,
  NewsArticleJsonLd,
  OrganizationJsonLd,
  ProductJsonLd,
  SiteLinksSearchBoxJsonLd,
} from 'next-seo'
import Head from 'next/head'
import { useMemo } from 'react'

interface Props {
  item?: any
  listing?: any
  type?:
    | 'organization'
    | 'breadcrumbs'
    | 'faq'
    | 'news'
    | 'search'
    | 'prodotto'
    | 'product_aggregate_offer'
    | 'itemList'
}

const JsonLd = (props: Props) => {
  const { item = undefined, listing = undefined, type = '' } = props

  switch (type) {
    case 'organization': {
      return (
        <OrganizationJsonLd
          type="Organization"
          name={BRAND}
          legalName="Emak S.p.A."
          id={`${API_HOST}`}
          url={`${API_HOST}`}
          logo={`${API_HOST}/static/images/logo.png`}
          image={`${API_HOST}/static/images/logo.png`}
          address={{
            streetAddress: 'Via E. Fermi, 4 - 42011 Bagnolo in Piano (Reggio Emilia)',
            addressLocality: 'Bagnolo in Piano',
            addressRegion: 'RE',
            postalCode: '42011',
            addressCountry: 'IT',
          }}
          sameAs={
            process.env.SITE_ID === '2'
              ? [
                  'http://www.facebook.com/OleoMacItalia',
                  'http://www.youtube.com/user/MyOleomac',
                  'https://www.instagram.com/OleomacItalia/',
                ]
              : process.env.SITE_ID === '1'
              ? [
                  'http://www.facebook.com/EfcoItalia',
                  'http://www.youtube.com/user/MyEfco',
                  'https://www.instagram.com/EfcoItalia/',
                ]
              : []
          }
        />
      )
    }
    case 'breadcrumbs': {
      return <BreadcrumbJsonLd itemListElements={item} />
    }
    case 'faq': {
      return <FAQPageJsonLd mainEntity={item} />
    }
    case 'news': {
      return <NewsArticleJsonLd {...item} />
    }
    case 'search': {
      return (
        <SiteLinksSearchBoxJsonLd
          url={`${API_HOST}`}
          potentialActions={[
            {
              target: `${API_HOST}?q`,
              queryInput: 'search_term_string',
            },
          ]}
        />
      )
    }
    case 'prodotto': {
      return (
        <ProductJsonLd
          productName={`${item.nome} ${item.categoria?.boxTitolo} ${BRAND}`}
          description={item.descrizione?.replace(/<[^>]*>?/gm, '')}
          images={item.detailImages?.map((x) => x.image)}
          sku={item.codice}
          mpn={item.rif}
          offers={[
            {
              price: item.prezzoScontato || item.prezzo,
              priceCurrency: 'EUR',
              itemCondition: 'https://schema.org/NewCondition',
              availability: `https://schema.org/${item.disponibile ? 'InStock' : 'OutOfStock'}`,
              url: `${API_HOST}${item.url}`,
              seller: {
                name: BRAND,
              },
            },
          ]}
        />
      )
    }
    case 'product_aggregate_offer': {
      if (!listing || !listing.elementi || listing.elementi.length === 0) {
        return null
      }

      const aggregateOffer = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        'name': listing.elementi[0]?.categoria?.boxTitolo,
        'url': listing.canonicalUrl,
        'image': [],
        'offers': {
          '@type': 'AggregateOffer',
          'offerCount': listing.paginator?.numItems || listing.elementi.length,
          'priceCurrency': 'EUR',
          'lowPrice': listing.priceMin,
          'highPrice': listing.priceMax,
          'url': listing.canonicalUrl,
        },
      }
      return (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(aggregateOffer),
            }}
          />
        </Head>
      )
    }
    case 'itemList': {
      if (!listing || !listing.elementi || listing.elementi.length === 0) {
        return null
      }

      const structuredData = useMemo(
        () => ({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          'itemListElement': listing.elementi.map((product, index) => ({
            '@type': 'ListItem',
            'position': index + 1,
            'item': {
              '@type': 'Product',
              'name': product.nome,
              'image': product.previewImage,
              'url': `${API_HOST}${product.url}`,
              'sku': product.codice,
              'brand': {
                '@type': 'Brand',
                'name': BRAND,
              },
              'offers': {
                '@type': 'Offer',
                'priceCurrency': 'EUR',
                'price': product.prezzoScontato || product.prezzo,
                'availability': product.disponibile
                  ? 'https://schema.org/InStock'
                  : 'https://schema.org/OutOfStock',
                'url': `${API_HOST}${product.url}`,
              },
            },
          })),
        }),
        [listing.elementi]
      )
      return (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(structuredData),
            }}
          />
        </Head>
      )
    }
    default: {
      return null
    }
  }
}

export default JsonLd
